import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
	LexicalTypeaheadMenuPlugin,
	MenuOption,
} from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as ReactDOM from "react-dom";
import { $createMentionNode } from "../nodes/MentionNode";

const mentionsCache = new Map();

class MentionTypeaheadOption extends MenuOption {
	constructor({
		id,
		name,
		username,
		email,
		avatar,

	}) {
		super(name);
		this.id = id;
		this.name = name;
		this.avatar = avatar;
		this.username = username;
		this.email = email;
	}
}

function MentionsTypeaheadMenuItem({ index, isSelected, onClick, onMouseEnter, option }) {
	return (
		<li
			key={option.id}
			tabIndex={-1}
			className={`flex items-center gap-3 px-4 py-2 rounded cursor-pointer transition-colors duration-150 ${isSelected && 'bg-primary text-white'}`}
			ref={option.setRefElement}
			role="option"
			aria-selected={isSelected}
			id={"typeahead-item-" + index}
			onMouseEnter={onMouseEnter}
			onClick={onClick}
		>
			{option.avatar && <img src={option.avatar} alt="" className="w-6 h-6 rounded-full" />}
			<span className="text">{option.name}</span>
		</li>
	);
}

function useMentionLookupService(query, api) {
	const [results, setResults] = useState([]);

	useEffect(() => {
		const cached = mentionsCache.get(query);
		if (query === null) {
			setResults([]);
			return;
		}
		if (cached !== undefined) {
			setResults(cached);
			return;
		}

		mentionsCache.set(query, null);
		const qParam = encodeURIComponent(query);
		const url = `/api/get-ms-topics-users/${window.MS_ID}/${window.threads.namespace}?q=${qParam}`;
		fetch(url)
			.then(res => res.json())
			.then(data => {
				const mapped = data.map(user => new MentionTypeaheadOption({
					id: user.id,
					name: user.full_name,
					username: user.user_name,
					avatar: user.avatar_path,
					email: user.email,
				}));
				mentionsCache.set(query, mapped);
				setResults(mapped);
			})
			.catch(() => setResults([]));
	}, [query, api]);

	return results;
}

function checkForAtSignMentions(text) {
	const match = text.match(/(^|\s)(@)([^\s@]*)$/);
	if (match) {
		return {
			leadOffset: match.index + match[1].length,
			matchingString: match[3],
			replaceableString: match[2] + match[3],
		};
	}
	return null;
}

export default function NewMentionsPlugin({ api = '' }) {
	const [editor] = useLexicalComposerContext();
	const [queryString, setQueryString] = useState("");

	const options = useMentionLookupService(queryString, api);

	const checkForMentionMatch = useCallback(
		text => {
			return checkForAtSignMentions(text);
		},
		[checkForAtSignMentions, editor]
	);

	const onSelectOption = useCallback(
		(selectedOption, nodeToReplace, closeMenu) => {
			editor.update(() => {
				const mentionNode = $createMentionNode(selectedOption.name, selectedOption.id);
				if (nodeToReplace) nodeToReplace.replace(mentionNode);
				mentionNode.select();
				closeMenu();
			});
		},
		[editor]
	);

	return (
		<LexicalTypeaheadMenuPlugin
			onQueryChange={setQueryString}
			onSelectOption={onSelectOption}
			triggerFn={checkForMentionMatch}
			options={options}
			// onClose={(e) => console.log('Mentions menu closed', e)}
			preselectFirstItem
			menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
				anchorElementRef.current && options?.length
					? ReactDOM.createPortal(
						<div
							className="typeahead-popover mentions-menu bg-white text-black rounded-lg w-60 shadow-primary"
						>
							<ul className="max-h-72 overflow-y-auto p-2" onWheel={e => { e.stopPropagation(); }}
								onScroll={e => { e.stopPropagation(); }}>
								{options.map((option, i) => (
									<MentionsTypeaheadMenuItem
										index={i}
										isSelected={selectedIndex === i}
										onClick={() => {
											setHighlightedIndex(i);
											selectOptionAndCleanUp(option);
										}}
										onMouseEnter={() => setHighlightedIndex(i)}
										key={option.id}
										option={option}
									/>
								))}
							</ul>
						</div>,
						anchorElementRef.current
					)
					: null
			}

		/>
	);
}
